import { useState, useEffect, useCallback } from 'react';
import { supabase, clearSupabaseAuthStorage, isInvalidRefreshTokenError } from '../services/supabaseClient';
import { getResetPasswordRedirectUrl, getConfirmEmailRedirectUrl } from '../utils/authRedirect';
import type { User, AuthError } from '@supabase/supabase-js';

export const AUTH_EMAIL_RATE_LIMIT_MESSAGE =
  '驗證信寄送次數過多，請稍候幾分鐘後再試，或聯絡 LensBank 協助開通帳號。';

export function isSupabaseAuthRateLimitError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as {
    message?: unknown;
    status?: unknown;
    code?: unknown;
    name?: unknown;
  };
  const message = String(candidate.message || '').toLowerCase();
  const code = String(candidate.code || '').toLowerCase();
  const status = Number(candidate.status);
  return (
    status === 429 ||
    message.includes('email rate limit exceeded') ||
    (message.includes('rate limit') && message.includes('email')) ||
    code.includes('rate_limit') ||
    code.includes('over_email_send_rate_limit')
  );
}

export function getCustomerAuthErrorMessage(error: unknown, fallback: string): string {
  if (isSupabaseAuthRateLimitError(error)) {
    return AUTH_EMAIL_RATE_LIMIT_MESSAGE;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
}

interface SignUpData {
  email: string;
  password: string;
  name?: string;
  phone?: string;
  company?: string;
  avatar?: string;
}

interface SignInData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    initialized: false
  });

  useEffect(() => {
    const initSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          if (isInvalidRefreshTokenError(error)) {
            console.warn('[useAuth] invalid refresh token detected, clearing auth storage.');
            clearSupabaseAuthStorage();
          } else {
            console.warn('[useAuth] getSession error:', error.message);
          }
          setAuthState({ user: null, loading: false, initialized: true });
          return;
        }
        setAuthState({
          user: session?.user ?? null,
          loading: false,
          initialized: true
        });
      } catch (err) {
        if (isInvalidRefreshTokenError(err)) {
          console.warn('[useAuth] stale refresh token detected, clearing auth storage.');
          clearSupabaseAuthStorage();
        } else {
          console.error('[useAuth] unexpected getSession error:', err);
        }
        setAuthState({ user: null, loading: false, initialized: true });
      }
    };

    initSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthState({
        user: session?.user ?? null,
        loading: false,
        initialized: true
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = useCallback(async (data: SignUpData) => {
    try {
      const { email, password, name, phone, company, avatar } = data;
      const { data: authData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: getConfirmEmailRedirectUrl(),
          data: {
            name: name || '',
            phone: phone || '',
            company: company || '',
            avatar: avatar || ''
          }
        }
      });
      if (error) throw error;
      return { data: authData, error: null };
    } catch (error) {
      return { data: null, error: error as AuthError };
    }
  }, []);

  const signIn = useCallback(async (data: SignInData) => {
    try {
      const { email, password } = data;
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      return { data: authData, error: null };
    } catch (error) {
      return { data: null, error: error as AuthError };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getResetPasswordRedirectUrl()
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  const updateUser = useCallback(async (updates: { name?: string; phone?: string; company?: string }) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as AuthError };
    }
  }, []);

  return {
    user: authState.user,
    loading: authState.loading,
    initialized: authState.initialized,
    isAuthenticated: !!authState.user,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUser
  };
}